import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { TimezoneSelect } from 'components/ui/timezone-select';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    referralCode?: string;
    referralSlug?: string;
    prefillData?: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
    };
    referredByTherapistId?: string;
    // Called instead of just showing the inline error when the backend blocks
    // new-account creation (admin-toggled "patient_signup" feature flag).
    // Existing users are never affected — this only fires on the signup path.
    // Passes the email the visitor already typed so the waitlist form can be
    // pre-filled instead of asking them to type it again.
    onJoinWaitlist?: (email: string) => void;
}

interface AuthState {
    email: string;
    phoneNumber: string;
    verificationCode: string;
    firstName: string;
    lastName: string;
    authMethod: 'email' | 'phone' | null;
    stage: 'method' | 'verification' | 'signup' | 'phone' | 'profile';
    isLoading: boolean;
    error: string;
    signupDisabled: boolean;
    selectedCountry: Country;
    showCountrySelect: boolean;
    timezone: string;
}

interface Country {
    code: string;
    name: string;
    flag: string;
    dialCode: string;
}

const countries: Country[] = [
    { code: 'CI', name: 'Côte d\'Ivoire', flag: '🇨🇮', dialCode: '+225' },
    { code: 'SN', name: 'Sénégal', flag: '🇸🇳', dialCode: '+221' },
    { code: 'BJ', name: 'Bénin', flag: '🇧🇯', dialCode: '+229' },
    { code: 'CD', name: 'RD Congo', flag: '🇨🇩', dialCode: '+243' },
    { code: 'CM', name: 'Cameroun', flag: '🇨🇲', dialCode: '+237' }
];

const defaultCountry = countries[0];

const initialState: AuthState = {
    email: '',
    phoneNumber: '',
    verificationCode: '',
    firstName: '',
    lastName: '',
    authMethod: 'email', // TODO: change to null when we have SMS verification
    stage: 'method',
    isLoading: false,
    error: '',
    signupDisabled: false,
    selectedCountry: defaultCountry, // Côte d'Ivoire by default
    showCountrySelect: false,
    timezone: 'Africa/Abidjan', // Default timezone
};

export const AuthModal = ({ isOpen, onClose, referralCode, referralSlug, prefillData, referredByTherapistId, onJoinWaitlist }: AuthModalProps) => {
    const { t, i18n } = useTranslation();
    const [state, setState] = useState<AuthState>({
        ...initialState,
        email: prefillData?.email || '',
        firstName: prefillData?.firstName || '',
        lastName: prefillData?.lastName || '',
    });
    const modalRef = useRef<HTMLDivElement>(null);
    const countrySelectRef = useRef<HTMLDivElement>(null);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, handleClose]);

    // Removed click-outside handler to prevent accidental closes during sign-up flow
    // Users can still close via Escape key or the Exit button

    // Handle click outside for country selector
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countrySelectRef.current && !countrySelectRef.current.contains(event.target as Node)) {
                setState(prev => ({ ...prev, showCountrySelect: false }));
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setState({
                ...initialState,
                email: prefillData?.email || '',
                firstName: prefillData?.firstName || '',
                lastName: prefillData?.lastName || '',
            });
        }
    }, [isOpen, prefillData]);

    if (!state.selectedCountry) {
        setState(prev => ({ ...prev, selectedCountry: defaultCountry }));
        return null; // Prevent rendering until state is initialized
    }

    const handleMethodSelect = (method: 'email' | 'phone') => {
        setState(prev => ({ ...prev, authMethod: method, error: '' }));
    };

    const handleRequestCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setState(prev => ({ ...prev, isLoading: true, error: '', signupDisabled: false }));

        try {
            const payload: Record<string, string | undefined> = {
                email: state.authMethod === 'email' ? state.email : undefined,
                phoneNumber: state.authMethod === 'phone' ? state.phoneNumber : undefined,
                language: i18n.language,
            };
            if (referralCode) payload.referralCode = referralCode;
            if (referralSlug) payload.referralSlug = referralSlug;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.code === 'SIGNUP_DISABLED') {
                    setState(prev => ({ ...prev, error: data.error || 'Une erreur est survenue', signupDisabled: true }));
                    return;
                }
                throw new Error(data.error || 'Une erreur est survenue');
            }

            setState(prev => ({ ...prev, stage: 'verification' }));
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Une erreur est survenue'
            }));
        } finally {
            setState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setState(prev => ({ ...prev, isLoading: true, error: '' }));

        try {
            const payload = {
                email: state.email,
                phoneNumber: state.phoneNumber,
                code: state.verificationCode
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Une erreur est survenue');
            }

            if (data.isNewUser) {
                // Store token early so /intake can make authenticated API calls
                localStorage.setItem('token', data.token);
                data.user.type = "patient";
                if (referredByTherapistId) {
                    data.user.referredByTherapistId = referredByTherapistId;
                }
                localStorage.setItem('user', JSON.stringify(data.user));
                // New users go through intake wizard first
                window.location.href = '/intake';
                handleClose();
            } else {
                localStorage.setItem('token', data.token);
                // TODO: The correct type should come from the backend
                data.user.type = "patient"
                localStorage.setItem('user', JSON.stringify(data.user));
                sessionStorage.setItem('justLoggedIn', 'true');
                window.location.href = '/home';
                handleClose();
            }
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Une erreur est survenue'
            }));
        } finally {
            setState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handlePhoneSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setState(prev => ({ ...prev, isLoading: true, error: '' }));

        try {
            const payload = {
                email: state.email,
                phoneNumber: `${state.selectedCountry.dialCode}${state.phoneNumber}`,
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/complete-phone`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Une erreur est survenue');
            }

            setState(prev => ({ ...prev, stage: 'profile' }));
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Une erreur est survenue'
            }));
        } finally {
            setState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setState(prev => ({ ...prev, isLoading: true, error: '' }));

        try {
            const phoneWithCode = state.phoneNumber
                ? `${state.selectedCountry.dialCode}${state.phoneNumber}`
                : undefined;
            const payload = {
                email: state.authMethod === 'email' ? state.email : undefined,
                phoneNumber: phoneWithCode,
                firstName: state.firstName,
                lastName: state.lastName,
                timezone: state.timezone
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/complete-profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Une erreur est survenue');
            }

            localStorage.setItem('token', data.token);
            // TODO: The correct type should come from the backend
            data.user.type = "patient"
            localStorage.setItem('user', JSON.stringify(data.user));
            // After profile completion, go to home (therapist already assigned via backend)
            window.location.href = '/home';
            handleClose();
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Une erreur est survenue'
            }));
        } finally {
            setState(prev => ({ ...prev, isLoading: false }));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-500 
      ease-in-out flex items-center justify-center z-50">
            <div ref={modalRef}
                className={`bg-white p-8 rounded-lg shadow-xl w-96 transform transition-all 
          duration-500 ease-out ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
            >
                {state.stage === 'method' && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-center" style={{ color: '#00c4cc' }}>
                            {t('auth.title')}
                        </h2>
                        <form onSubmit={handleRequestCode} className="space-y-4">
                            <Input
                                type="email"
                                placeholder={t('auth.emailPlaceholder')}
                                value={state.email}
                                onChange={(e) => setState(prev => ({ ...prev, email: e.target.value, error: '', signupDisabled: false }))}
                                className={state.error ? 'border-red-500' : ''}
                                disabled={state.isLoading}
                            />

                            {state.error && (
                                <p className="text-red-500 text-sm">{state.error}</p>
                            )}

                            {state.signupDisabled && onJoinWaitlist && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => onJoinWaitlist(state.email)}
                                >
                                    {t('waitlist.join')}
                                </Button>
                            )}

                            <Button
                                type="submit"
                                className="w-full text-white"
                                style={{ backgroundColor: '#00c4cc' }}
                                disabled={state.isLoading || !state.email || state.signupDisabled}
                            >
                                {state.isLoading ? t('auth.sending') : t('auth.sendCode')}
                            </Button>
                        </form>
                    </div>
                )}

                {state.stage === 'verification' && (
                    <form onSubmit={handleVerifyCode} className="space-y-6">
                        <h2 className="text-2xl font-bold text-center" style={{ color: '#00c4cc' }}>
                            {t('auth.verificationTitle')}
                        </h2>
                        <p className="text-gray-600 text-center">
                            {t('auth.verificationText')}{' '}
                            <span className="font-medium">
                                {state.authMethod === 'email' ? state.email : state.phoneNumber}
                            </span>
                        </p>

                        <Input
                            type="text"
                            placeholder={t('auth.codePlaceholder')}
                            value={state.verificationCode}
                            onChange={(e) => setState(prev => ({
                                ...prev,
                                verificationCode: e.target.value.replace(/\D/g, '').slice(0, 6),
                                error: ''
                            }))}
                            className={state.error ? 'border-red-500 text-center text-2xl tracking-wider' : 'text-center text-2xl tracking-wider'}
                            disabled={state.isLoading}
                            maxLength={6}
                        />

                        {state.error && (
                            <p className="text-red-500 text-sm text-center">{state.error}</p>
                        )}

                        <Button
                            type="submit"
                            className="w-full text-white"
                            style={{ backgroundColor: '#00c4cc' }}
                            disabled={state.isLoading || state.verificationCode.length !== 6}
                        >
                            {state.isLoading ? t('auth.verifying') : t('auth.verify')}
                        </Button>
                    </form>
                )}

                {state.stage === 'phone' && (
                    <form onSubmit={handlePhoneSubmit} className="space-y-6">
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-center" style={{ color: '#00c4cc' }}>
                                {t('auth.welcomeTitle')}
                            </h2>
                            <p className="text-gray-600 text-center" dangerouslySetInnerHTML={{ __html: t('auth.welcomeText') }} />

                            <div className="space-y-2">
                                <div className="flex gap-2">
                                    <div className="relative" ref={countrySelectRef}>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-24 flex items-center justify-between gap-2"
                                            onClick={() => setState(prev => ({ ...prev, showCountrySelect: !prev.showCountrySelect }))}
                                        >
                                            <span>{state.selectedCountry.flag}</span>
                                            <span>{state.selectedCountry.dialCode}</span>
                                        </Button>

                                        {/* Country selector dropdown */}
                                        {state.showCountrySelect && (
                                            <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-10">
                                                {countries.map((country) => (
                                                    <button
                                                        key={country.code}
                                                        type="button"
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                                                        onClick={() => {
                                                            setState(prev => ({
                                                                ...prev,
                                                                selectedCountry: country,
                                                                showCountrySelect: false
                                                            }));
                                                        }}
                                                    >
                                                        <span>{country.flag}</span>
                                                        <span>{country.name}</span>
                                                        <span className="text-gray-500 ml-auto">{country.dialCode}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <Input
                                        type="tel"
                                        placeholder={t('auth.phonePlaceholder')}
                                        value={formatPhoneNumberWithoutCode(state.phoneNumber)}
                                        onChange={(e) => {
                                            // Extract only digits from the input
                                            const inputValue = e.target.value;
                                            const digitsOnly = inputValue.replace(/\D/g, '');
                                            
                                            // Limit to 10 digits
                                            const limitedDigits = digitsOnly.slice(0, 10);

                                            setState(prev => ({
                                                ...prev,
                                                phoneNumber: limitedDigits,
                                                error: ''
                                            }));
                                        }}
                                        onKeyDown={(e) => {
                                            // Prevent spacebar from doing anything
                                            if (e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        }}
                                        className={`flex-1 ${state.error ? 'border-red-500' : ''} 
                      ${isValidPhoneNumber(state.phoneNumber) ? 'border-green-500' : ''}`}
                                        disabled={state.isLoading}
                                    />
                                </div>

                                {state.error && (
                                    <p className="text-red-500 text-sm text-center">{state.error}</p>
                                )}
                            </div>

                            <Button
                                type="submit"
                                className="w-full text-white"
                                style={{ backgroundColor: '#00c4cc' }}
                                disabled={state.isLoading || !isValidPhoneNumber(state.phoneNumber)}
                            >
                                {state.isLoading ? t('auth.saving') : t('auth.save')}
                            </Button>
                        </div>
                    </form>
                )}

                {state.stage === 'signup' && (
                    <div className="space-y-6">
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#00c4cc] to-[#00a3ab] rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>

                            <h2 className="text-2xl font-bold" style={{ color: '#00c4cc' }}>
                                {t('auth.signup.title')}
                            </h2>

                            <p className="text-lg font-medium text-gray-700">
                                {t('auth.signup.subtitle')}
                            </p>

                            <p className="text-gray-600">
                                {t('auth.signup.description')}
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-[#00c4cc] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                                <p className="text-sm text-gray-700">{t('auth.signup.step1')}</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-[#00c4cc] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                                <p className="text-sm text-gray-700">{t('auth.signup.step2')}</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-[#00c4cc] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                                <p className="text-sm text-gray-700">{t('auth.signup.step3')}</p>
                            </div>
                        </div>

                        <Button
                            onClick={() => setState(prev => ({ ...prev, stage: 'profile' }))}
                            className="w-full text-white"
                            style={{ backgroundColor: '#00c4cc' }}
                        >
                            {t('auth.signup.continue')}
                        </Button>
                    </div>
                )}

                {state.stage === 'profile' && (
                    <form onSubmit={handleProfileSubmit} className="space-y-6">
                        <h2 className="text-2xl font-bold text-center" style={{ color: '#00c4cc' }}>
                            {t('auth.profileTitle')}
                        </h2>
                        <p className="text-gray-600 text-center">
                            {t('auth.profileText')}
                        </p>

                        <Input
                            type="text"
                            placeholder={t('auth.firstNamePlaceholder')}
                            value={state.firstName}
                            onChange={(e) => setState(prev => ({ ...prev, firstName: e.target.value, error: '' }))}
                            className={state.error ? 'border-red-500' : ''}
                            disabled={state.isLoading}
                            autoFocus
                        />

                        <Input
                            type="text"
                            placeholder={t('auth.lastNamePlaceholder')}
                            value={state.lastName}
                            onChange={(e) => setState(prev => ({ ...prev, lastName: e.target.value, error: '' }))}
                            className={state.error ? 'border-red-500' : ''}
                            disabled={state.isLoading}
                        />

                        <div className="space-y-2">
                            <label className="text-sm text-gray-500 flex items-center gap-1.5">
                                <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                WhatsApp
                            </label>
                            <div className="flex gap-2">
                                <div className="relative" ref={countrySelectRef}>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-24 flex items-center justify-between gap-2"
                                        onClick={() => setState(prev => ({ ...prev, showCountrySelect: !prev.showCountrySelect }))}
                                    >
                                        <span>{state.selectedCountry.flag}</span>
                                        <span>{state.selectedCountry.dialCode}</span>
                                    </Button>

                                    {state.showCountrySelect && (
                                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-10">
                                            {countries.map((country) => (
                                                <button
                                                    key={country.code}
                                                    type="button"
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                                                    onClick={() => {
                                                        setState(prev => ({
                                                            ...prev,
                                                            selectedCountry: country,
                                                            showCountrySelect: false
                                                        }));
                                                    }}
                                                >
                                                    <span>{country.flag}</span>
                                                    <span>{country.name}</span>
                                                    <span className="text-gray-500 ml-auto">{country.dialCode}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <Input
                                    type="tel"
                                    placeholder={t('auth.phonePlaceholder')}
                                    value={formatPhoneNumberWithoutCode(state.phoneNumber)}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setState(prev => ({ ...prev, phoneNumber: digitsOnly, error: '' }));
                                    }}
                                    onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                                    className={`flex-1 ${state.error ? 'border-red-500' : ''} ${isValidPhoneNumber(state.phoneNumber) ? 'border-green-500' : ''}`}
                                    disabled={state.isLoading}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm text-gray-500">Fuseau horaire</label>
                            <TimezoneSelect
                                value={state.timezone}
                                onValueChange={(value) => setState(prev => ({ ...prev, timezone: value }))}
                            />
                        </div>

                        {state.error && (
                            <p className="text-red-500 text-sm text-center">{state.error}</p>
                        )}

                        <Button
                            type="submit"
                            className="w-full text-white"
                            style={{ backgroundColor: '#00c4cc' }}
                            disabled={state.isLoading || !state.firstName.trim() || !state.lastName.trim() || !isValidPhoneNumber(state.phoneNumber)}
                        >
                            {state.isLoading ? t('auth.registering') : t('auth.start')}
                        </Button>
                    </form>
                )}

                <Button
                    onClick={handleClose}
                    className="w-full mt-6"
                    variant="outline"
                    disabled={state.isLoading}
                >
                    {t('auth.exit')}
                </Button>
            </div>
        </div>
    );
};

const formatPhoneNumberWithoutCode = (number: string): string => {
    if (!number) return '';

    // Group digits in pairs
    const matches = number.match(/.{1,2}/g);
    return matches ? matches.join(' ') : number;
};

const isValidPhoneNumber = (number: string): boolean => {
    const digitsOnly = number.replace(/\D/g, '');
    return digitsOnly.length === 8 || digitsOnly.length === 10;
};
